'use client';
import { type RefObject } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { IconArrowLeftDouble } from '@/icons/IconArrowLeftDouble';
import { IconArrowRightDouble } from '@/icons/IconArrowRightDouble';
import { SliderArrow } from '@/components/SliderArrow';
import { ICollectableCoupon } from '@/interfaces/collectableCoupon';
import { chunkByPage, chunkByRow, type GridResult } from '../lib/grid';
import { CouponCard } from './CouponCard';

export interface CollectableCouponSwiperProps {
  coupons: ICollectableCoupon[];
  grid: GridResult;
  /** Viewport column tier (1/2/3/4) from `useCollectableCouponLayout`. */
  pageSize: number;
  pageIndex: number;
  onPageChange: (pageIndex: number) => void;
  /** Used by the modal to call `slideToLoop` programmatically after a Collect. */
  swiperRef: RefObject<any>;
  onCollect: (couponId: string) => void;
  isCollecting: boolean;
  collectingId?: string;
  onCardClose?: () => void;
  /** Show the mobile card-level close icon on every card. */
  showCardCloseIcon?: boolean;
}

/**
 * Maps `(cols, pageSize)` to a swiper width class so the visible column
 * count matches `grid.cols`. Width = `cols × cardWidth + (cols-1) × gap`
 * (gap = 19.2px). Card widths: 230 (mobile), 180 (xs), 200 (sm), 240 (md+).
 * Full literal Tailwind classes are returned so the JIT picks them up.
 */
const computeSwiperWidthClass = (cols: number, pageSize: number): string => {
  if (pageSize === 1) return 'w-[230px]';

  if (pageSize === 2) {
    if (cols === 1) return 'xs:w-[180px] sm:w-[200px]';
    return 'xs:w-[379.2px] sm:w-[419.2px]';
  }

  if (pageSize === 3) {
    if (cols === 1) return 'md:w-[240px]';
    if (cols === 2) return 'md:w-[499.2px]';
    if (cols === 3) return 'md:w-[758.4px]';
    return 'md:w-[1017.6px]';
  }

  if (cols === 1) return 'xl:w-[240px]';
  if (cols === 2) return 'xl:w-[499.2px]';
  if (cols === 3) return 'xl:w-[758.4px]';
  return 'xl:w-[1017.6px]';
};

/**
 * Encapsulates the Swiper + prev/next arrows + per-page row layout.
 * The modal/popup pages are split into rows of `grid.cols` so a
 * partial last row is centered (e.g. 4 cards @ cols=2, rows=2 → 2 rows
 * of 2 centered).
 */
export const CollectableCouponSwiper = ({
  coupons,
  grid,
  pageSize,
  pageIndex,
  onPageChange,
  swiperRef,
  onCollect,
  isCollecting,
  collectingId,
  onCardClose,
  showCardCloseIcon = false,
}: CollectableCouponSwiperProps) => {
  const pages = chunkByPage(coupons, grid.slidesPerPage);
  const swiperWidthClass = computeSwiperWidthClass(grid.cols, pageSize);

  const goPrev = () => pageIndex > 0 && onPageChange(pageIndex - 1);
  const goNext = () => pageIndex < grid.totalPages - 1 && onPageChange(pageIndex + 1);

  return (
    <div className="relative flex max-h-[75vh] min-w-0 flex-col items-center pb-2 xs:flex-row xs:items-stretch xs:justify-center">
      <Swiper
        key={`${grid.cols}-${grid.rows}-${coupons.length}`}
        modules={[Navigation]}
        slidesPerView={1}
        slidesPerGroup={1}
        spaceBetween={0}
        centeredSlides={false}
        centeredSlidesBounds={false}
        navigation={{ prevEl: '.floating-coupon-swipe-prev', nextEl: '.floating-coupon-swipe-next' }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper) => {
          // Keep pageIndex in sync with the swiper's real position
          // so subsequent advance-after-collect calls compute the
          // correct target.
          if (typeof swiper.realIndex === 'number') {
            onPageChange(swiper.realIndex);
          }
        }}
        className={`overflow-hidden max-w-full scrollbar-hide floating-coupon-swiper w-[230px] ${swiperWidthClass}`.trim()}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        data-testid="floating-coupon-swiper"
      >
        {pages.map((pageCoupons, pageIdx) => {
          const rowChunks = chunkByRow(pageCoupons, grid.cols);
          return (
            <SwiperSlide key={pageIdx} data-testid={`floating-coupon-page-${pageIdx}`}>
              <div className="flex flex-col items-center gap-[1.2rem]">
                {rowChunks.map((row, rowIdx) => (
                  <div
                    key={rowIdx}
                    className={`flex w-full justify-start gap-[1.2rem]`}
                    data-testid={`floating-coupon-page-${pageIdx}-row-${rowIdx}`}
                  >
                    {row.map((coupon) => (
                      <CouponCard
                        key={coupon.Id}
                        coupon={coupon}
                        onCollect={onCollect}
                        isCollecting={isCollecting}
                        collectingId={collectingId}
                        showCloseIcon={showCardCloseIcon}
                        onClose={onCardClose}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      <div
        className="floating-coupon-swipe-prev swipe-prev pointer-events-none absolute inset-y-0 left-0 z-10"
        data-testid="floating-coupon-prev-arrow"
      >
        <SliderArrow
          aria-label="Previous coupons"
          onClick={goPrev}
          className="cursor-pointer rounded-full !p-2.5 pointer-events-auto !absolute !left-[-40px] !top-1/2 !-translate-y-1/2"
          style={{ background: 'transparent !important' }}
          data-testid="floating-coupon-prev-arrow-button"
        >
          <IconArrowLeftDouble className="h-6 w-6 text-white xs:text-black" />
        </SliderArrow>
      </div>
      <div
        className="floating-coupon-swipe-next swipe-next pointer-events-none absolute inset-y-0 right-0 z-10"
        data-testid="floating-coupon-next-arrow"
      >
        <SliderArrow
          aria-label="Next coupons"
          onClick={goNext}
          className="cursor-pointer rounded-full !p-2.5 pointer-events-auto !absolute !right-[-40px] !top-1/2 !-translate-y-1/2"
          style={{ background: 'transparent !important' }}
          data-testid="floating-coupon-next-arrow-button"
        >
          <IconArrowRightDouble className="h-6 w-6 text-white xs:text-black" />
        </SliderArrow>
      </div>
    </div>
  );
};